import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphQLClient } from "../graphqlClient";
import { GET_CMS_BANNERS } from "../api/queries";
import { usePathname } from "../routes/hooks/use-pathname";
import { useProductFeatures } from "./useProductFeatures";
import { getFeatureFlag } from "./useFeatureFlag";
import type { CMSBanner, CMSGetBannersResult } from "../types/cms-types";
import type { BannerData } from "@hats-and-ladders/client-common";

const devCacheKey = Math.round(Date.now() / 1000);

/**
 * Convert a CMS path pattern to a RegExp.
 * - `**` matches any number of path segments (including zero)
 * - `*` matches exactly one path segment
 */
function pathPatternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\*\*/g, "<<GLOBSTAR>>")
    .replace(/\*/g, "[^/]+")
    .replace(/<<GLOBSTAR>>/g, ".*");
  return new RegExp(`^${escaped}$`);
}

function matchesPath(patterns: string[] | null, pathname: string): boolean {
  if (!patterns || patterns.length === 0) return true;
  return patterns.some((pattern) => pathPatternToRegex(pattern).test(pathname));
}

function isWithinDateRange(
  startDate: string | null,
  endDate: string | null,
): boolean {
  const now = Date.now();
  if (startDate && new Date(startDate).getTime() > now) return false;
  if (endDate && new Date(endDate).getTime() < now) return false;
  return true;
}

export function useBanners(): { banners: BannerData[]; loading: boolean } {
  const pathname = usePathname();
  const { matchesProductPlans } = useProductFeatures();

  const isCacheEnabled = getFeatureFlag("CMS_CACHE", true);
  const cacheKey = isCacheEnabled ? 0 : devCacheKey;

  const { data, isLoading } = useQuery<CMSGetBannersResult>({
    queryKey: ["cmsBanners", cacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_BANNERS, {
        variables: { cacheKey },
      }),
    staleTime: isCacheEnabled ? 1000 * 60 * 10 : 0,
    gcTime: 1000 * 60 * 60 * 4,
  });

  const banners = useMemo(() => {
    const allBanners = data?.cmsBanners || [];
    return allBanners
      .filter((b: CMSBanner) => {
        if (!b.active) return false;
        if (!isWithinDateRange(b.startDate, b.endDate)) return false;
        if (!matchesPath(b.paths, pathname)) return false;
        if (
          b.productPlans &&
          b.productPlans.length > 0 &&
          !matchesProductPlans(b.productPlans)
        )
          return false;
        return true;
      })
      .map(
        (b: CMSBanner): BannerData => ({
          id: b.id,
          title: b.title,
          description: b.description,
          image: b.image,
        }),
      );
  }, [data, pathname, matchesProductPlans]);

  return { banners, loading: isLoading };
}
